import * as React from 'react'
import {ChangeEvent, Component, ReactElement} from 'react'
import {Box} from '@mui/material'
import {NatApplicationRepo} from '../../repository/NatApplicationRepo'
import {BlockSpacer} from '../../../../../styledComponents/ResponsiveConvenience'
import {FlexContainer, FullWidthColumnFlexContainer} from '../../../../../styledComponents/FlexComponents'
import {convertToRoomNumberForResident} from '../../../../common/util/Natutil'
import NatCurrentDateTimeRepo from '../../../currentDateTime/repository/NatCurrentDateTimeRepo'
import moment from 'moment'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {ApplyButton, ItemHeading, WideWhiteArea} from '../../common/resident/NatResidentApplicationsCommon'
import {NatGetCurrentDateTime} from '../../../currentDateTime/repository/NatGetCurrentDateTime'
import {NatResidentConstructionCompleteLocationState} from '../NatResidentApplicationInterface'
import {Nullable} from '../../../../../app/TypeHelpers'
import EventEmitterExResident from '../../../../../components/shared/pageBaseComponents/EventEmitterExResident'
import NatCondominiumRepo from '../../../condominium/repository/NatCondominiumRepo'
import NatResidentApplicationDetailFormItem from '../../common/resident/NatResidentApplicationDetailFormItem'
import HolidaysRepository from '../../../../../components/HolidaysRepository'
import {NatPutConstructionCompleteRequest} from '../../repository/NatPutConstructionComplete'
import {AsyncCallFunctionType} from '../../../../../components/shared/hoc/withAsyncCommunication'
import {PPTextFieldForResident} from '../../../../../components/shared/forms/resident/PPTextFieldForResident'
import {HKConstants} from '../../../../../HKConstants'
import PPDatePickerForResident from '../../../../../components/shared/forms/resident/PPDatePickerForResident'
import {Location, NavigateFunction} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    condominiumRepo: NatCondominiumRepo
    holidaysRepo: HolidaysRepository
    navigate: NavigateFunction
    location: Location<NatResidentConstructionCompleteLocationState>
    eventEmitter: EventEmitterExResident
    asyncCallFunction: AsyncCallFunctionType

    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    contractorName: Nullable<string>
    constructionCompleteDate: Nullable<string>

    submitButtonLabel: string
    onClickApplyConfirmButton: (request: NatPutConstructionCompleteRequest) => Promise<void>
}

interface State {
    applicantFamilyName: string
    applicantFirstName: string
    contractorName: string
    constructionCompleteDate: string

    currentDate: moment.Moment
    holidays: string[]

    applyButtonDisabled: boolean
    isLoadCompleted: boolean
}

export default class NatResidentConstructionCompleteForm extends Component<Props, State> {

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            applyButtonDisabled: true,
            applicantFamilyName: '',
            applicantFirstName: '',
            contractorName: '',
            constructionCompleteDate: '',
            currentDate: moment(),
            holidays: [],
            isLoadCompleted: false,
        }

        this.handleChangeText = this.handleChangeText.bind(this)
        this.handleChangeCompleteDate = this.handleChangeCompleteDate.bind(this)
        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        try {
            const [currentTimeResponse, holidays] = await Promise.all([
                this.getCurrentDateTime(),
                this.getHoliday(),
            ])

            if (currentTimeResponse && currentTimeResponse.details) {
                this.setState({
                    currentDate: moment(currentTimeResponse.details.currentDateTime),
                })
            }

            const applicantFamilyName = this.props.applicantFamilyName ?? this.state.applicantFamilyName
            const applicantFirstName = this.props.applicantFirstName ?? this.state.applicantFirstName
            const contractorName = this.props.contractorName ?? this.state.contractorName
            const constructionCompleteDate = this.props.constructionCompleteDate ?? this.state.constructionCompleteDate

            this.setState({
                applicantFamilyName: applicantFamilyName,
                applicantFirstName: applicantFirstName,
                contractorName: contractorName,
                constructionCompleteDate: constructionCompleteDate,
                holidays: holidays === null ? [] : holidays,
            }, () => {
                this.updateApplyConfirmButtonDisabled()
            })
        } finally {
            this.setState({isLoadCompleted: true})
        }
        this.props.eventEmitter.closeLoading()
    }

    async getCurrentDateTime(): Promise<Nullable<NatGetCurrentDateTime>> {
        const {currentDateTimeRepo, eventEmitter} = this.props

        return await this.props.asyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    async getHoliday(): Promise<Nullable<string[]>> {
        const {holidaysRepo, eventEmitter} = this.props

        const minDate: string = HKConstants.CMN.SYC.MIN_HOLIDAY_DATE
        const maxDate: string = HKConstants.CMN.SYC.MAX_HOLIDAY_DATE
        return await this.props.asyncCallFunction(
            holidaysRepo.getHolidays.bind(holidaysRepo, minDate, maxDate),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    handleChangeText(event: ChangeEvent<HTMLInputElement>): void {
        const key = event.target.name as keyof State
        const value: string = event.target.value
        this.setState({...this.state, [key]: value}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    handleChangeCompleteDate(date: string): void {
        this.setState({constructionCompleteDate: date}, () => {
            this.updateApplyConfirmButtonDisabled()
        })
    }

    updateApplyConfirmButtonDisabled(): void {
        const {
            applicantFamilyName, applicantFirstName, constructionCompleteDate,
        } = this.state

        let applyButtonDisabled = false

        if (!applicantFamilyName || !applicantFirstName || !constructionCompleteDate) {
            applyButtonDisabled = true
        }
        this.setState({applyButtonDisabled: applyButtonDisabled})
    }

    async onClickApplyConfirmButton(): Promise<void> {
        const {
            applicantFamilyName, applicantFirstName, contractorName, constructionCompleteDate,
        } = this.state

        let request: Nullable<NatPutConstructionCompleteRequest>

        request = {
            applicantFamilyName: applicantFamilyName,
            applicantFirstName: applicantFirstName,
            contractorName: contractorName,
            constructionCompleteDate: constructionCompleteDate,
            confirmed: false,
        }

        if (!request) {
            return
        }
        await this.props.onClickApplyConfirmButton(request)
    }

    renderApplicantForm(): ReactElement {
        const {applicantFamilyName, applicantFirstName} = this.state
        const applicantRoom = NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_CONDOMINIUM_NAME) + '　' +
            NatResidentLoginInfoSessionStorageHelper
                .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_RIDGENAME) + ' ' +
            convertToRoomNumberForResident(
                NatResidentLoginInfoSessionStorageHelper
                    .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.SELECTED_ROOM_NUMBER),
            )

        return (
            <>
                <ItemHeading>
                    申請者
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    {applicantRoom}
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={'氏名'}
                        required
                        component={
                            <FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFamilyName',
                                        maxLength: 10,
                                        value: applicantFamilyName,
                                        placeholder: '例：素敵',
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFamilyName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                                <FlexContainer>
                                    <BlockSpacer width={5}/>
                                </FlexContainer>
                                <PPTextFieldForResident
                                    inputProps={{
                                        name: 'applicantFirstName',
                                        maxLength: 10,
                                        placeholder: '例：太郎',
                                        value: applicantFirstName,
                                    }}
                                    onChange={this.handleChangeText}
                                    eventKeys={['applicantFirstName']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </FlexContainer>
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderContractorNameForm(): ReactElement {
        const {contractorName} = this.state

        return (
            <>
                <ItemHeading>
                    工事施工者
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={<span>業者名</span>}
                        component={
                            <PPTextFieldForResident
                                inputProps={{
                                    maxLength: 50,
                                    name: 'contractorName',
                                    placeholder: '例：株式会社長谷工リフォーム',
                                    value: contractorName,
                                }}
                                onChange={this.handleChangeText}
                                eventKeys={['contractorName']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    renderConstructionCompleteDateForm(): ReactElement {
        const {constructionCompleteDate, holidays} = this.state

        return (
            <>
                <ItemHeading>
                    工事完了日
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <NatResidentApplicationDetailFormItem
                        title={<span>工事が完了した日付</span>}
                        required
                        component={
                            <PPDatePickerForResident
                                date={constructionCompleteDate}
                                onChange={this.handleChangeCompleteDate}
                                holidays={holidays}
                                itemValue={''}
                                eventKeys={['constructionCompleteDate']}
                                eventEmitter={this.props.eventEmitter}
                            />
                        }
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
            </>
        )
    }

    render(): ReactElement {
        const {submitButtonLabel} = this.props
        const {applyButtonDisabled} = this.state

        if (!this.state.isLoadCompleted) {
            return (<></>)
        }
        return (
            <>
                {this.renderApplicantForm()}
                < Box height={'15px'}/>
                {this.renderContractorNameForm()}
                < Box height={'15px'}/>
                {this.renderConstructionCompleteDateForm()}
                <ApplyButton disabled={applyButtonDisabled} onClick={this.onClickApplyConfirmButton}>
                    <FullWidthColumnFlexContainer>
                        <div>
                            {submitButtonLabel}
                        </div>
                    </FullWidthColumnFlexContainer>
                </ApplyButton>
            </>
        )
    }
}
